import { apiSlice } from "../api/apiSlice";
import getPaginationInfo from "../../utils/getPaginationInfo";
import paramConfig from "../../config/paramConfig";

/**
 * Slim list-row shape returned by `GET /conclusions`. Anything not listed here
 * (linked feature_ids, etc.) requires the per-id detail endpoint.
 * @typedef {Object} ConclusionSummary
 * @property {string} id
 * @property {string} name
 * @property {string} level1
 * @property {string} level2
 * @property {string} level3
 * @property {string} aspect
 * @property {string} destination
 * @property {Array<Record<string, unknown>>} rules
 * @property {string} date_created
 * @property {string} date_modified
 */

/**
 * Full conclusion shape returned by `GET /conclusions/:id`. Includes everything
 * in ConclusionSummary plus relational fields (linked features, etc.).
 * @typedef {ConclusionSummary & Record<string, unknown>} Conclusion
 */

export const conclusionsSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // List endpoint — returns ConclusionSummary[]. Do NOT pre-seed the per-id
    // detail cache from these entries: the slim shape would mask a needed
    // detail fetch and detail pages would render with missing fields.
    getConclusions: builder.query({
      query: ({ page = 1, pageSize = 600, sortBy }) =>
        `/conclusions?${paramConfig.PAGE_NUMBER}=${page}&${
          paramConfig.PAGE_SIZE
        }=${pageSize}${sortBy ? paramConfig.SORT_BY + "=" + sortBy : ""}`,
      transformResponse: (response, meta) => {
        const pagination = getPaginationInfo(meta);
        return { response, pagination };
      },
      providesTags: (result, error, arg) => [
        { type: "Conclusion" },
        { type: "Conclusion", id: arg?.page },
        { type: "Conclusion", id: arg?.pageSize },
        { type: "Conclusion", id: arg?.sortBy || "unsorted" },
      ],
    }),
    // Detail endpoint — returns the full Conclusion.
    getConclusion: builder.query({
      query: ({ id }) => `/conclusions/${id}`,
      providesTags: (result, error, arg) => [
        { type: "Conclusion", id: arg?.id },
      ],
    }),
    postConclusion: builder.mutation({
      query: ({ body }) => {
        return {
          url: `/conclusions`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: [{ type: "Conclusion" }],
    }),
    deleteConclusion: builder.mutation({
      query: ({ params }) => {
        return {
          url: `/conclusions/${params?.id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "Conclusion" }],
    }),
    patchConclusion: builder.mutation({
      query: ({ params, body }) => {
        return {
          url: `/conclusions/${params?.conclusionId}`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Conclusion" },
        { type: "Conclusion", id: arg?.params?.conclusionId },
      ],
    }),
    postFeatureForConclusion: builder.mutation({
      query: ({ params, body }) => {
        return {
          url: `/conclusions/${params?.conclusionId}/features`,
          method: "POST",
          body,
        };
      },
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Feature" },
              { type: "Feature", id: body?.feature_id },
            ])
          );
        } catch (err) {
          console.error(err);
        }
      },
    }),
    deleteFeatureForConclusion: builder.mutation({
      query: ({ params }) => {
        return {
          url: `/conclusions/${params?.conclusionId}/features/${params?.featureId}`,
          method: "DELETE",
        };
      },
      async onQueryStarted({ params }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Feature", id: params?.featureId },
            ])
          );
        } catch (err) {
          console.error(err);
        }
      },
    }),
  }),
});

export const {
  useGetConclusionsQuery,
  useGetConclusionQuery,
  usePostConclusionMutation,
  useDeleteConclusionMutation,
  usePatchConclusionMutation,
  usePostFeatureForConclusionMutation,
  useDeleteFeatureForConclusionMutation,
} = conclusionsSlice;
